'use client';

/**
 * AddSavedAddressDialog - Dialog for adding a new saved crypto address
 * 
 * Allows users to save external crypto wallet addresses for quick withdrawals.
 * Uses Sheet pattern for mobile-first UX.
 * 
 * @see useSavedAddresses hook
 */

import { NetworkIcon } from '@/components/NetworkIcons';
import { Button } from '@/components/ui/button';
import { CurrencyIcon } from '@/components/ui/CurrencyIcon';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { SFIcon } from '@/components/ui/sf-icon';
import { Heading, Text } from '@/components/ui/typography';
import { useIsMobile } from '@/hooks/use-mobile';
import { CHAIN_CURRENCIES, SUPPORTED_CHAINS, SUPPORTED_CURRENCIES, useAddSavedAddress } from '@/hooks/useSavedAddresses';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { InlineLoadingIndicator } from '@/components/ui/inline-loading';
import { useState } from 'react';

interface AddSavedAddressDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function AddSavedAddressDialog({ open, onClose, onSuccess }: AddSavedAddressDialogProps) {
  const isMobile = useIsMobile();
  const addAddress = useAddSavedAddress();

  // Form state
  const [label, setLabel] = useState('');
  const [address, setAddress] = useState('');
  const [chain, setChain] = useState('ethereum');
  const [currency, setCurrency] = useState('usdc');

  // Get available currencies for selected chain
  const availableCurrencies = SUPPORTED_CURRENCIES.filter(
    c => CHAIN_CURRENCIES[chain]?.includes(c.value)
  );

  // Reset form
  const resetForm = () => {
    setLabel('');
    setAddress('');
    setChain('ethereum');
    setCurrency('usdc');
  };

  // When chain changes, reset currency if not available
  const handleChainChange = (newChain: string) => {
    setChain(newChain);
    const chainCurrencies = CHAIN_CURRENCIES[newChain] || ['usdc'];
    if (!chainCurrencies.includes(currency)) {
      setCurrency(chainCurrencies[0] || 'usdc');
    }
  };

  // Handle close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!label.trim() || !address.trim() || !chain || !currency) return;

    try {
      await addAddress.mutateAsync({
        label: label.trim(),
        address: address.trim(),
        chain,
        currency,
      });

      handleClose();
      onSuccess?.();
    } catch {
      // Error handled by mutation
    }
  };

  // Validate address format (basic check)
  const isAddressValid = (addr: string, selectedChain: string) => {
    if (!addr) return false;

    // Basic validation by chain
    switch (selectedChain) {
      case 'ethereum':
      case 'base':
      case 'polygon':
      case 'arbitrum':
      case 'optimism':
      case 'avalanche':
        return /^0x[a-fA-F0-9]{40}$/.test(addr);
      case 'solana':
        return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(addr);
      case 'tron':
        return /^T[a-zA-Z0-9]{33}$/.test(addr);
      case 'stellar':
        return /^G[A-Z0-9]{55}$/.test(addr);
      default:
        return addr.length > 20;
    }
  };

  const canSubmit = label.trim() && address.trim() && chain && currency && isAddressValid(address.trim(), chain);

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        className={cn(
          // Layout only (glass styles are in base SheetContent)
          isMobile ? "h-[90vh] rounded-t-[32px] rounded-l-none" : "w-[28rem] max-w-[28rem]",
          semanticTokens.motion.standard
        )}
      >
        {/* Header */}
        <SheetHeader className="flex-shrink-0 pb-4 border-b">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="min-h-[44px] min-w-[44px] rounded-xl"
            >
              <SFIcon icon={sfIcons.back} className="h-5 w-5" />
            </Button>
            <div className={cn(
              'p-2.5 rounded-xl',
              semanticTokens.warning.bg
            )}>
              <SFIcon icon={sfIcons.wallet} className={cn('h-5 w-5', semanticTokens.warning.accent)} />
            </div>
            <div>
              <SheetTitle asChild>
                <Heading level={3}>Add Wallet Address</Heading>
              </SheetTitle>
              <SheetDescription asChild>
                <Text variant="caption" color="muted">
                  Save for quick withdrawals
                </Text>
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden py-6 space-y-6 min-w-0">
          {/* Label */}
          <div className="space-y-2">
            <Label className={semanticTokens.typography.label}>Label</Label>
            <Input
              placeholder="e.g. My Ledger, Binance, MetaMask"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={50}
              className={cn(
                'min-h-[52px] rounded-xl',
                semanticTokens.motion.standard,
                semanticTokens.focus.ring
              )}
            />
            <Text variant="caption" color="muted">
              A friendly name to identify this wallet
            </Text>
          </div>

          {/* Chain */}
          <div className="space-y-2">
            <Label className={semanticTokens.typography.label}>Network</Label>
            <Select value={chain} onValueChange={handleChainChange}>
              <SelectTrigger className="min-h-[52px] rounded-xl">
                <SelectValue>
                  <div className="flex items-center gap-2">
                    <NetworkIcon network={chain} className="h-5 w-5" />
                    <span>{SUPPORTED_CHAINS.find(c => c.value === chain)?.label || chain}</span>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent
                className="z-[10000]"
                position="popper"
                sideOffset={4}
              >
                {SUPPORTED_CHAINS.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    <div className="flex items-center gap-2">
                      <NetworkIcon network={c.value} className="h-5 w-5" />
                      <span>{c.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Currency/Token */}
          <div className="space-y-2">
            <Label className={semanticTokens.typography.label}>Token</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="min-h-[52px] rounded-xl">
                <SelectValue>
                  <div className="flex items-center gap-2">
                    <CurrencyIcon currency={currency} size="sm" />
                    <span>{SUPPORTED_CURRENCIES.find(c => c.value === currency)?.label || currency.toUpperCase()}</span>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent
                className="z-[10000]"
                position="popper"
                sideOffset={4}
              >
                {availableCurrencies.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    <div className="flex items-center gap-2">
                      <CurrencyIcon currency={c.value} size="sm" />
                      <span>{c.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Text variant="caption" color="muted">
              Available tokens on {SUPPORTED_CHAINS.find(c => c.value === chain)?.label}
            </Text>
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label className={semanticTokens.typography.label}>Wallet Address</Label>
            <Input
              placeholder={
                chain === 'solana'
                  ? 'Enter Solana address...'
                  : chain === 'tron'
                    ? 'Enter TRON address (T...)'
                    : 'Enter 0x address...'
              }
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className={cn(
                'min-h-[52px] rounded-xl font-mono text-sm',
                semanticTokens.motion.standard,
                semanticTokens.focus.ring,
                address && !isAddressValid(address, chain) && 'border-red-500'
              )}
            />
            {address && !isAddressValid(address, chain) && (
              <Text variant="caption" className="text-red-500">
                Invalid address format for {chain}
              </Text>
            )}
            <Text variant="caption" color="muted">
              Double-check the address — transactions are irreversible
            </Text>
          </div>

          {/* Warning */}
          <div className={cn(
            'p-4 rounded-xl',
            semanticTokens.warning.bg,
            semanticTokens.warning.border,
            'border'
          )}>
            <Text variant="sm" className={semanticTokens.warning.fg}>
              ⚠️ Only send to addresses you control. Supabank cannot recover funds sent to wrong addresses.
            </Text>
          </div>
        </div>

        {/* Footer */}
        <SheetFooter className="flex-shrink-0 pt-4 border-t gap-3">
          <Button
            variant="ghost"
            onClick={handleClose}
            className={cn(
              "min-h-[52px] flex-1 rounded-xl",
              semanticTokens.motion.standard
            )}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || addAddress.isPending}
            className={cn(
              "min-h-[52px] flex-1 rounded-xl gap-2",
              semanticTokens.motion.standard
            )}
          >
            {addAddress.isPending ? (
              <InlineLoadingIndicator size="sm" />
            ) : (
              <SFIcon icon={sfIcons.add} className="h-4 w-4" />
            )}
            Save Address
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

